/**
 * The row above the account list: the list's heading and the
 * "All | Assigned | Unassigned" picker on the left, the account search box on
 * the right. The picker also changes the headline numbers; search only filters
 * the list. The picker only shows when the user has more than one choice
 * (admins); members see just the "Assigned to You" heading.
 */

import { useTranslation } from "react-i18next";
import type { BookScope } from "@onsemble/shared/types/sales-insights";
import { AccountScope } from "../../shared/AccountScope";

/** The list's heading for each picker choice */
const HEADINGS: Record<BookScope, string> = {
  // "Assigned": every rep's accounts, for admins
  assigned: "pages.pcInsights.list.assignedAccounts",
  // "Unassigned"
  house: "pages.pcInsights.list.houseAccounts",
  // "All accounts"
  all: "pages.pcInsights.list.allAccounts",
};

/** A member's only heading: "Assigned to You" */
const MEMBER_HEADING = "pages.pcInsights.list.assignedToYou";

export function Controls({
  scope,
  scopes,
  onScopeChange,
  query,
  onQueryChange,
}: {
  /** The picked choice; also picks the heading */
  scope: BookScope;
  /** The choices this user has; one choice means no picker */
  scopes: BookScope[];
  onScopeChange: (next: BookScope) => void;
  query: string;
  onQueryChange: (next: string) => void;
}) {
  const { t } = useTranslation();
  // One choice means a member, who gets their own heading
  const heading = (s: BookScope) => (scopes.length > 1 ? HEADINGS[s] : MEMBER_HEADING);

  return (
    <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
      {/* Heading for the list, with the picker beside it */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Every heading the user can get sits in the same grid cell; the unused
            ones are invisible, so the heading is always as wide as the longest */}
        <h2 className="m-0 inline-grid text-si-xl font-semibold leading-[1.2] tracking-[-0.015em]">
          {scopes.map((s) => (
            // Hidden unless it's the picked choice's heading
            <span
              key={s}
              className={`col-start-1 row-start-1 ${s === scope ? "" : "invisible"}`}
              aria-hidden={s !== scope}
            >
              {t(heading(s))}
            </span>
          ))}
        </h2>
        {/* Only when there's something to pick */}
        {scopes.length > 1 && (
          <AccountScope scope={scope} scopes={scopes} onChange={onScopeChange} />
        )}
      </div>
      {/* Search box; filters the list by account name as you type. Its border darkens while typing */}
      <label className="flex min-h-11 items-center gap-2 rounded-full border border-transparent bg-si-surface px-3.5 text-si-ink-2 focus-within:border-si-brand max-sm:w-full">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder={t("pages.pcInsights.controls.searchPlaceholder")}
          aria-label={t("pages.pcInsights.controls.searchPlaceholder")}
          // No browser search styling, outline or ring on the field itself: the
          // pill's border shows focus instead, so no box appears inside it
          className="w-[180px] appearance-none border-0 bg-transparent text-si-md text-si-ink shadow-none outline-none ring-0 placeholder:text-si-ink-3 focus:outline-none focus:ring-0 focus-visible:outline-none max-sm:w-full [&::-webkit-search-decoration]:appearance-none"
        />
      </label>
    </div>
  );
}
